import React, { useEffect, useState } from "react";
import ApexCharts from "apexcharts";
import jsonData from "../../../../current-survey-numeric.json";
import "../../../../pages/D04_Responses_by_Demographic_Category.css";

const StackedBarChart = ({
  selectedQuestion,
  selectedDemographic,
  filterCriteria,
}) => {
  const [chartData, setChartData] = useState({
    favorable: [],
    neutral: [],
    unfavorable: [],
    noResponse: [],
  });
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const getSortedFieldValues = (fieldName, order = []) => {
      const values = [
        ...new Set(jsonData.map((item) => item[fieldName])),
      ].filter(Boolean);
      if (order.length > 0) {
        return values.sort((a, b) => order.indexOf(a) - order.indexOf(b));
      }
      return values.sort((a, b) => a.localeCompare(b));
    };

    const demographicValues =
      selectedDemographic === "length_of_service_name"
        ? getSortedFieldValues("length_of_service_name", [
            "Less than 1 year",
            "1 to 2 years",
            "3 to 5 years",
            "6 to 10 years",
            "11 to 19 years",
            "20 years or more",
          ])
        : selectedDemographic === "generation_name"
        ? getSortedFieldValues("generation_name", [
            "Traditionalist",
            "Baby Boomer",
            "Generation X",
            "Millennial",
            "Generation Z",
            "Generation Next",
            "No Response",
          ])
        : getSortedFieldValues(selectedDemographic);

    if (!demographicValues || demographicValues.length === 0) {
      setCategories([]);
      setChartData({
        noResponse: [],
        unfavorable: [],
        neutral: [],
        favorable: [],
      });
      return;
    }

    // Calculate total responses across all demographics
    const globalTotals = {
      favorable: 0,
      neutral: 0,
      unfavorable: 0,
      noResponse: 0,
    };

    jsonData.forEach((response) => {
      const answer = response[selectedQuestion];
      if (answer === 4 || answer === 5) {
        globalTotals.favorable++;
      } else if (answer === 3) {
        globalTotals.neutral++;
      } else if (answer === 1 || answer === 2) {
        globalTotals.unfavorable++;
      } else {
        globalTotals.noResponse++;
      }
    });

    const totalResponses =
      Object.values(globalTotals).reduce((sum, val) => sum + val, 0) || 1; // Avoid division by zero

    const data = demographicValues.reduce(
      (acc, value) => {
        const filteredData = filterDataByCriteria(
          jsonData,
          selectedDemographic,
          value,
          filterCriteria
        );

        let favorable = 0;
        let neutral = 0;
        let unfavorable = 0;
        let noResponse = 0;

        filteredData.forEach((response) => {
          const answer = response[selectedQuestion];
          if (answer === 4 || answer === 5) {
            favorable++;
          } else if (answer === 3) {
            neutral++;
          } else if (answer === 1 || answer === 2) {
            unfavorable++;
          } else {
            noResponse++;
          }
        });

        // Convert counts to percentages relative to the global total
        acc.noResponse.push((noResponse / totalResponses) * 100);
        acc.unfavorable.push((unfavorable / totalResponses) * 100);
        acc.neutral.push((neutral / totalResponses) * 100);
        acc.favorable.push((favorable / totalResponses) * 100);

        return acc;
      },
      { noResponse: [], unfavorable: [], neutral: [], favorable: [] }
    );

    setChartData(data);
    setCategories(demographicValues);
  }, [selectedQuestion, selectedDemographic, filterCriteria]);

  useEffect(() => {
    const barHeight = 20;
    const chartHeight = categories.length * barHeight + 100;

    const stackedBarsOptions = {
      series: [
        { name: "No Response", data: chartData.noResponse },
        { name: "Unfavorable", data: chartData.unfavorable },
        { name: "Neutral", data: chartData.neutral },
        { name: "Favorable", data: chartData.favorable },
      ],
      chart: {
        type: "bar",
        height: chartHeight,
        stacked: true,
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: "20px",
        },
      },
      xaxis: {
        categories: categories,
        title: {
          text: "Percentage of Total Responses", // Updated axis title
        },
      },
      tooltip: {
        y: {
          formatter: (val) => `${val.toFixed(2)}%`, // Show two decimals in tooltips
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${val.toFixed(2)}%`, // Show percentages on the bars
        style: {
          fontSize: "12px",
          colors: ["#fff"], // Adjust as needed
        },
      },
      colors: ["#bab0ac", "#f28e2b", "#f1ce63", "#499894"],
    };

    const chart = new ApexCharts(
      document.querySelector("#stacked-bar-chart"),
      stackedBarsOptions
    );
    chart.render();

    return () => chart.destroy();
  }, [chartData, categories]);

  const filterDataByCriteria = (
    data,
    demographicType,
    demographicValue,
    criteria
  ) => {
    return data.filter((item) => {
      if (!item[demographicType] || item[demographicType] === null)
        return false; // Skip null values
      let match = true;
      Object.keys(criteria).forEach((key) => {
        if (
          criteria[key].length > 0 &&
          (!item[key] || !criteria[key].includes(item[key]))
        ) {
          match = false;
        }
      });
      return match && item[demographicType] === demographicValue;
    });
  };

  return (
    <div className="D04_Responses_by_Demographic_Category-internal-charts">
      <div className="D04_Responses_by_Demographic_Category-internal-chart-container">
        <div
          id="stacked-bar-chart"
          className="D04_Responses_by_Demographic_Category-internal-chart"
        ></div>
      </div>
    </div>
  );
};

export default StackedBarChart;
